import styled from '@emotion/styled';

import {
    BodyTextMedium,
    Button,
    ButtonGroup,
    HeadingText20,
    HeadingText50,
    RadioGroup,
    pxToRem,
} from '@modernhealth/vital-components';

import first from 'lodash/first';
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, Redirect } from 'react-router';
import { useDispatch } from 'react-redux';
import queryString from 'query-string';

import { navigationService } from 'src/config/rootService';
import {
    CareNavigationRequestSource,
    CareNavigationRequestTopic,
    DeviceCategory,
} from 'src/generated/webapp_gql';
import { useTopicSelectionGroupsQuery } from 'src/modules/user/documents/topicSelectionGroupsQuery.generated';
import { SOURCE_TYPES } from 'src/modules/user/models/user';
import { mediaQueries } from 'src/shared/styles/variables';
import { Dispatch } from 'src/config/rootStore';

import { CareNavigationSteps } from '../components/CareNavigationSteps';
import { useIsCareNavEnabledUser } from '../hooks/useIsCareNavEnabledUser';
import { CareNavigationRequest } from '../models/careNavigation';
import { ButtonGroupTestID } from '../models/testsIds';

const Wrapper = styled.div({
    display: 'flex',
    flexDirection: 'column',
    maxWidth: pxToRem(500),
    marginTop: pxToRem(100),
    marginRight: 'auto',
    marginLeft: 'auto',
    [mediaQueries.sm]: {
        padding: '0.5rem',
    },
});

const ButtonGroupContainer = styled.div({
    marginTop: pxToRem(24),
    marginBottom: pxToRem(40),
});

const TitleContainer = styled.div({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'left',
    width: '100%',
    marginBottom: pxToRem(24),
    marginTop: pxToRem(32),
});

const FormContainer = styled.div({
    width: '100%',
    [mediaQueries.sm]: {
        paddingBottom: 0,
    },
});

const CrisisElementsContainer = styled.div({
    display: 'flex',
    flexDirection: 'row',
});

const CrisisLinkContainer = styled.div({
    paddingLeft: pxToRem(12),
    cursor: 'pointer',
    fontColor: 'blue55',
});

export const CareNavigationNeedStep: React.FC = () => {
    const location = useLocation<CareNavigationRequest>();
    const { t } = useTranslation();
    const dispatch = useDispatch<Dispatch>();
    const { data } = useTopicSelectionGroupsQuery({});

    const topicSelectionGroup = first(data?.topicSelectionGroups);

    const hasAtLeastOneClinicalSubtopicOfFocus =
        topicSelectionGroup?.hasAtLeastOneClinicalSubtopicOfFocus;

    const initialFlowValue = hasAtLeastOneClinicalSubtopicOfFocus ? 'singleClinical' : 'normal';

    const determineFlow = (value: CareNavigationRequestTopic) => {
        if (value !== CareNavigationRequestTopic.TopicsOfFocus) {
            return hasAtLeastOneClinicalSubtopicOfFocus ? 'singleClinical' : 'normal';
        } else {
            if (subTopicsOfFocus && subTopicsOfFocus?.length > 1) {
                return 'multipleClinical';
            } else {
                return 'singleClinical';
            }
        }
    };

    const urlParams = queryString.parse(location.search);
    const paramSource = urlParams?.source;

    const source =
        location?.state?.source ?? paramSource ?? CareNavigationRequestSource.WellbeingReport;

    const [requestState, setRequestState] = useState<CareNavigationRequest>({
        ...location.state,
        source,
        topic: location?.state?.topic ?? '',
        platform: DeviceCategory.Web,
        message: location?.state?.message ?? '',
        contactPreference: location?.state?.contactPreference ?? '',
        flow: location?.state?.flow ?? initialFlowValue,
    });

    const { isUserEnabledFlow } = useIsCareNavEnabledUser();

    const subTopicsOfFocus = topicSelectionGroup?.subtopicSelections?.map(
        item => item?.subtopicOfFocus?.name,
    );

    const optionsNonClinical = [
        {
            label: t('careNavigation:care_navigation_need_step.radio_option_one'),
            value: CareNavigationRequestTopic.CompanyBenefits,
        },
        {
            label: t('careNavigation:care_navigation_need_step.radio_option_two'),
            value: CareNavigationRequestTopic.OneOnOneCare,
        },
        {
            label: t('careNavigation:care_navigation_need_step.radio_option_four'),
            value: CareNavigationRequestTopic.Other,
        },
    ];

    const optionsClinical = [
        {
            label: t('careNavigation:care_navigation_need_step.radio_option_one'),
            value: CareNavigationRequestTopic.CompanyBenefits,
        },
        {
            label: t('careNavigation:care_navigation_need_step.radio_option_two'),
            value: CareNavigationRequestTopic.OneOnOneCare,
        },
        {
            label: `${t(
                'careNavigation:care_navigation_need_step.radio_option_three',
            )} ${subTopicsOfFocus?.join(', ')}`,
            value: CareNavigationRequestTopic.TopicsOfFocus,
        },
        {
            label: t('careNavigation:care_navigation_need_step.radio_option_four'),
            value: CareNavigationRequestTopic.Other,
        },
    ];

    const isContinueButtonDisabled = () => {
        return !(requestState.topic !== '');
    };

    const handleContinue = () => {
        if (requestState.flow === 'multipleClinical') {
            navigationService.navigate('/care-navigation-topics', requestState);
        } else {
            navigationService.navigate('/care-navigation-goals', requestState);
        }
    };

    const handleBack = () => {
        if (requestState.source === CareNavigationRequestSource.BenefitsPage) {
            navigationService.navigate('/all-your-benefits');
        } else if (requestState.source === CareNavigationRequestSource.WellbeingReport) {
            navigationService.navigate('/wellbeing-report');
        } else if (requestState.source === CareNavigationRequestSource.CareOptions) {
            navigationService.navigate('/care-options');
        } else {
            navigationService.navigate('/dashboard');
        }
    };

    const handleRadioChange = (value: CareNavigationRequestTopic) => {
        const flow = determineFlow(value);
        const subTopicUUID = topicSelectionGroup?.subtopicSelections[0].subtopicOfFocus.uuid;
        setRequestState({
            ...requestState,
            subTopics: flow === 'singleClinical' ? [subTopicUUID] : [],
            topic: value,
            flow,
        });
    };

    useEffect(() => {
        dispatch.user.updateCrisisModalSource(SOURCE_TYPES.CALLBACK_SCREEN);
    }, [dispatch.user]);

    if (!isUserEnabledFlow) {
        return <Redirect to="/dashboard" />;
    }

    return (
        <Wrapper>
            <CareNavigationSteps step={1} flow={requestState.flow} />

            <TitleContainer>
                <HeadingText50>{t('careNavigation:care_navigation_need_step.title')}</HeadingText50>
            </TitleContainer>

            <BodyTextMedium marginBottom={32}>
                {t('careNavigation:care_navigation_need_step.description')}
            </BodyTextMedium>

            <FormContainer>
                <RadioGroup
                    onChange={value => handleRadioChange(value as CareNavigationRequestTopic)}
                    options={
                        hasAtLeastOneClinicalSubtopicOfFocus ? optionsClinical : optionsNonClinical
                    }
                    selected={requestState.topic}
                />

                <ButtonGroupContainer>
                    <ButtonGroup>
                        <Button
                            variant="secondary"
                            onClick={handleBack}
                            data-testid={ButtonGroupTestID.FirstButton}
                        >
                            {t('common.back')}
                        </Button>
                        <Button
                            variant="primary"
                            size="large"
                            onClick={handleContinue}
                            disabled={isContinueButtonDisabled()}
                            data-testid={ButtonGroupTestID.SecondButton}
                        >
                            {t('common.continue')}
                        </Button>
                    </ButtonGroup>
                </ButtonGroupContainer>

                <CrisisElementsContainer>
                    <BodyTextMedium>{t('shared:crisis_support_banner.message')}</BodyTextMedium>
                    <CrisisLinkContainer
                        onClick={() => {
                            dispatch.user.toogleShowCrisisModal();
                        }}
                    >
                        <HeadingText20 underline fontColor="blue55">
                            {t('careNavigation:care_navigation_need_step.view_crisis_resources')}
                        </HeadingText20>
                    </CrisisLinkContainer>
                </CrisisElementsContainer>
            </FormContainer>
        </Wrapper>
    );
};
